import { useEffect } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { LayoutDashboard, ToggleLeft, Users, Package, ShieldCheck, FileText, History, Printer, Star, GripVertical, Calendar, Inbox, Share2, Video, Home, Building2, ArrowUpDown } from "lucide-react";

const NAV = [
  { to: "/admin", end: true, label: "Dashboard", icon: LayoutDashboard },
  { to: "/admin/plugins", label: "Plugins & Settings", icon: ToggleLeft },
  { to: "/admin/users", label: "Users", icon: Users },
  { to: "/admin/art-hubs", label: "Art Hubs", icon: Building2 },
  { to: "/admin/audit", label: "Audit Log", icon: History },
  { to: "/admin/catalogue", label: "Catalogue", icon: Package },
  { to: "/admin/ordering", label: "Arrangement", icon: ArrowUpDown },
  { to: "/admin/content", label: "Content", icon: FileText },
  { to: "/admin/articles", label: "Article Queue", icon: Inbox },
  { to: "/admin/events", label: "Events Queue", icon: Calendar },
  { to: "/admin/social", label: "Social Marketing", icon: Share2 },
  { to: "/admin/featured", label: "Masters in Residence", icon: GripVertical },
  { to: "/admin/artist-of-the-week", label: "Artist of Week", icon: Star },
  { to: "/admin/intro-videos", label: "Intro Videos", icon: Video },
  { to: "/admin/rooms", label: "Room Scenes", icon: Home },
  { to: "/admin/flyer", label: "Flyer Generator", icon: Printer },
];

export default function AdminLayout() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (loading) return;
    if (!user) navigate("/login");
    else if (user.role !== "admin") navigate("/");
  }, [user, loading, navigate]);

  if (loading || !user || user.role !== "admin") {
    return <div className="p-24 label-eyebrow text-[#555]">Verifying access…</div>;
  }

  return (
    <div data-testid="admin-layout" className="min-h-screen bg-[#FAFAFA]">
      <div className="grid grid-cols-12 gap-0">
        {/* Sidebar */}
        <aside className="col-span-12 lg:col-span-3 xl:col-span-2 border-r border-[#E5E0D8] bg-white min-h-[calc(100vh-80px)]">
          <div className="p-6">
            <div className="inline-flex items-center gap-2 label-eyebrow bg-[#1E3A8A] text-[#F4C430] px-3 py-1.5">
              <ShieldCheck className="w-3.5 h-3.5" /> Admin Console
            </div>
            <h2 className="font-serif-display text-2xl mt-4 text-[#111]">
              Hello, {user.name.split(" ")[0]}
            </h2>
            <p className="text-xs text-[#555] mt-1 font-mono-aam">{user.email}</p>
          </div>
          <nav className="flex flex-col">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                data-testid={`admin-nav-${item.label.toLowerCase().split(" ")[0]}`}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-6 py-3.5 border-l-2 transition-colors label-eyebrow ${
                    isActive
                      ? "border-[#1E3A8A] text-[#1E3A8A] bg-[#F4EFE6]/50"
                      : "border-transparent text-[#555] hover:text-[#111]"
                  }`
                }
              >
                <item.icon className="w-4 h-4" strokeWidth={1.5} />
                {item.label}
              </NavLink>
            ))}
          </nav>
          <div className="mt-10 px-6">
            <Link
              to="/"
              className="label-eyebrow text-[#555] hover:text-[#1E3A8A]"
            >
              ← Back to site
            </Link>
          </div>
        </aside>

        {/* Main */}
        <main className="col-span-12 lg:col-span-9 xl:col-span-10 p-8 md:p-12">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
